import { useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Button, Grid, Stack } from '@mui/material'
import { ArrowForward } from '@mui/icons-material'

import { queries } from '../../graphql'
import { Box, Container, Text } from '../../components'
// ActionButton is a genuinely generic, reusable component (menu-metadata-driven,
// RBAC-gated, i18n-translated) with no admin-specific coupling -- reused here
// rather than duplicated, per the architect's explicit instruction that
// Initiate Transfer should be a MenuType.ACTION item, not hardcoded JSX.
import { ActionButton } from '../../management/management-components/actions/ActionButton'
import { RequestLiquidationDialog } from './initiate-transfer-dialog'
import { PortfolioSummary } from './portfolio-summary'
import { hasUnlinkedInvestmentActivity } from './investment-activity-helpers'
import { TotalBalanceCard, borderedBoxSx } from './total-balance-card'

function PortfolioCard({ portfolio, onRequestLiquidation }) {
  const navigate = useNavigate()

  return (
    <Box sx={{ ...borderedBoxSx, py: 3, px: 5, height: '100%' }}>
      <PortfolioSummary
        portfolio={portfolio}
        actions={
          <>
            <Button
              variant="text"
              endIcon={<ArrowForward fontSize="small" />}
              onClick={() => navigate(String(portfolio.id))}
            >
              View activity
            </Button>
            {/* FLIGHT-604 renamed/retired donor.investment_transfers.initiate_transfer
                -- request_liquidation is its direct replacement for this card. */}
            <ActionButton
              actionKey="donor.investment_transfers.request_liquidation"
              variant="text"
              onClick={() => onRequestLiquidation(portfolio)}
            />
          </>
        }
      />
    </Box>
  )
}

// A Contribution or Balance Adjustment whose portfolioId is null (a
// Contribution predating FLIGHT-299, pre-existing/legacy Balance Adjustment
// rows, or ones deliberately not tied to a specific account) isn't
// attributable to one specific Investment Account. Rather than duplicating
// these into every portfolio's own activity table, they get one shared card
// -- only shown at all when there's actually something in it.
function OtherActivityCard() {
  const navigate = useNavigate()
  return (
    <Box sx={{ ...borderedBoxSx, padding: 3, height: '100%' }}>
      <Stack spacing={1.5}>
        <Text.Body color="text.secondary">Transactions not linked to a specific Investment Account.</Text.Body>
        <Stack direction="row" spacing={2}>
          <Button variant="text" endIcon={<ArrowForward fontSize="small" />} onClick={() => navigate('other')}>
            View activity
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

export function InvestmentAccount() {
  const fundId = Number(useParams().fundId)
  const { loading, data } = useQuery(queries.funds.myFundInvestmentPortfolios, { variables: { fundId } })
  const fund = data?.me?.fund
  const portfolios = fund?.investmentPortfolios || []
  const hasUnlinkedActivity = hasUnlinkedInvestmentActivity(fund)

  if (loading) return null

  // A donor with at most one card's worth of investment activity to look at
  // -- one real Investment Account, some unlinked wallet activity but no
  // real Investment Account, or neither -- doesn't need the extra card +
  // View Activity click. Send them straight to that one drill-down, the
  // same page reached by clicking View Activity when there's more than one
  // card to choose from. A donor with nothing at all lands on the unlinked-
  // activity page too (portfolios.length is 0, so it falls to 'other'),
  // which renders an empty activity table rather than this page showing a
  // plain "no investment accounts yet" text.
  const cardCount = portfolios.length + (hasUnlinkedActivity ? 1 : 0)
  if (cardCount <= 1) {
    return <Navigate to={portfolios.length === 1 ? String(portfolios[0].id) : 'other'} replace />
  }

  return (
    <Container maxWidth="lg">
      {/* One shared Grid, not a Stack + separately-nested Grid -- keeps the
          total balance card and every portfolio card left-aligned against
          the exact same coordinate system instead of relying on two
          different layout containers happening to cancel out to the same
          edge. No wrapping CardBox -- the total balance, each portfolio, and
          the unlinked-activity card are each their own bordered box now, so
          a shared white/shadow panel around all of them would just be a
          second, redundant panel underneath. */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TotalBalanceCard amount={fund?.investmentWalletBalance} />
        </Grid>
        {/* cardCount > 1 to get here (see the redirect above), so at least
            one of these two always renders -- no separate "nothing yet"
            branch needed. */}
        {portfolios.map((portfolio) => (
          <Grid item xs={12} key={portfolio.id}>
            <PortfolioCardWithTransfer portfolio={portfolio} fundId={fundId} />
          </Grid>
        ))}
        {hasUnlinkedActivity && (
          <Grid item xs={12}>
            <OtherActivityCard />
          </Grid>
        )}
      </Grid>
    </Container>
  )
}

// Wraps each card with its own Request Liquidation dialog state -- kept
// per-portfolio (not one shared dialog at the page level) so the dialog
// always knows which portfolio it's liquidating without threading extra
// state up.
function PortfolioCardWithTransfer({ portfolio, fundId }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  return (
    <>
      <PortfolioCard portfolio={portfolio} onRequestLiquidation={() => setDialogOpen(true)} />
      <RequestLiquidationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fundId={fundId}
        portfolio={portfolio}
      />
    </>
  )
}
